import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import logodark from "../../assets/images/logo-dark.png";
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
} from "reactstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { resetPasswordUserAPI, resetPasswordEmployeeAPI } from "../../helpers/api.ts";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Get email and userType from location state
  const email = location.state?.email;
  const userType = location.state?.userType || "user";

  useEffect(() => {
    // Redirect if email is not provided
    if (!email) {
      navigate(userType === "employee" ? "/employee-forgot-password" : "/forgot-password");
    }
  }, [email, navigate, userType]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .required("Please Enter Your Password")
        .min(6, "Password must be at least 6 characters"),
      confirmPassword: Yup.string()
        .required("Please Confirm Your Password")
        .oneOf([Yup.ref("password")], "Passwords must match"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setErrorMessage("");
        setSuccessMessage("");
        
        const response = userType === "employee"
          ? await resetPasswordEmployeeAPI(email, values.password, values.confirmPassword)
          : await resetPasswordUserAPI(email, values.password, values.confirmPassword);
        
        if (response.success) {
          setSuccessMessage(response.message);
          // Navigate to login page after 2 seconds
          setTimeout(() => {
            navigate(userType === "employee" ? "/EmployeeLogin" : "/login");
          }, 2000);
        }
      } catch (error) {
        if (error.response) {
          const { data } = error.response;
          setErrorMessage(
            data.message || data.error || "An error occurred. Please try again."
          );
        } else {
          setErrorMessage("Unable to connect to the server. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    document.body.className = "bg-pattern";
    return () => {
      document.body.className = "";
    };
  }, []);

  if (!email) {
    return null; // Will redirect in useEffect
  }

  return (
    <React.Fragment>
      <div className="bg-overlay"></div>
      <div className="account-pages my-5 pt-5">
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} xl={4}>
              <Card>
                <CardBody className="p-4">
                  <div>
                    <div className="text-center">
                      <Link to="/">
                        <img
                          src={logodark}
                          alt="dark logo"
                          height="100"
                          className="auth-logo logo-dark mx-auto"
                        />
                        <img
                          src={logodark}
                          alt="light logo"
                          height="100"
                          className="auth-logo logo-light mx-auto"
                        />
                      </Link>
                    </div>
                    <h4 className="font-size-18 text-muted mt-2 text-center">
                      Reset Password
                    </h4>
                    <p className="mb-5 text-center">
                      Enter your new password below.
                    </p>
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {errorMessage && (
                        <Alert color="danger">
                          <div className="text-center">{errorMessage}</div>
                        </Alert>
                      )}
                      {successMessage && (
                        <Alert color="success">
                          <div className="text-center">{successMessage}</div>
                        </Alert>
                      )}
                      <Row>
                        <Col md={12}>
                          <div className="mb-4">
                            <Label className="form-label">New Password</Label>
                            <Input
                              name="password"
                              className="form-control"
                              placeholder="Enter new password"
                              type="password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.password || ""}
                              invalid={
                                validation.touched.password &&
                                validation.errors.password
                              }
                            />
                            {validation.touched.password &&
                              validation.errors.password && (
                                <FormFeedback type="invalid">
                                  {validation.errors.password}
                                </FormFeedback>
                              )}
                          </div>
                          <div className="mb-4">
                            <Label className="form-label">Confirm Password</Label>
                            <Input
                              name="confirmPassword"
                              className="form-control"
                              placeholder="Confirm new password"
                              type="password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.confirmPassword || ""}
                              invalid={
                                validation.touched.confirmPassword &&
                                validation.errors.confirmPassword
                              }
                            />
                            {validation.touched.confirmPassword &&
                              validation.errors.confirmPassword && (
                                <FormFeedback type="invalid">
                                  {validation.errors.confirmPassword}
                                </FormFeedback>
                              )}
                          </div>
                          <div className="d-grid mt-4">
                            <button
                              className="btn btn-primary waves-effect waves-light"
                              type="submit"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Resetting...
                                </>
                              ) : (
                                "Reset Password"
                              )}
                            </button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                    <div className="text-center mt-4">
                      <Link 
                        to={userType === "employee" ? "/EmployeeLogin" : "/login"} 
                        className="text-muted"
                      >
                        <i className="mdi mdi-arrow-left"></i> Back to Login
                      </Link>
                    </div>
                    <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-2 mb-4 text-center mt-4">
                      <Link to="/privacy-policy" className="text-muted">
                        Privacy Policy
                      </Link>
                      <span className="text-muted d-none d-md-inline">|</span>
                      <Link to="/terms-conditions" className="text-muted">
                        Terms & Conditions
                      </Link>
                      <span className="text-muted d-none d-md-inline">|</span>
                      <Link to="/contact-us" className="text-muted">
                        Contact Us
                      </Link>
                    </div>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-5 text-center">
                <p className="text-white-50">
                  © {new Date().getFullYear()} Crafted with{" "}
                  <i className="mdi mdi-heart text-danger"></i> by Govindani
                  Infotech Pvt. Ltd.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

ResetPassword.propTypes = {
  error: PropTypes.string,
};

export default ResetPassword;

